import React, { useEffect, useState } from 'react'
import { Prediction, Match } from '../types/match'
import { formatDate, getMatchDate } from '../utils/dateHelper'
import { useTranslation } from '../hooks/use-i18n'
import TeamNameWithFlag from './TeamNameWithFlag'

interface ExistedPredictionModalProps {
  existedPrediction: Prediction | null
  match: Match | null
  onKeepClick: () => void
  onChangeClick: () => void
}

const ExistedPredictionModal: React.FC<ExistedPredictionModalProps> = ({ 
  existedPrediction, 
  match,
  onChangeClick, 
  onKeepClick 
}) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (existedPrediction) {
      setTimeout(() => {
        setIsOpen(true)
      }, 200)
    }
  }, [existedPrediction])

  if (!isOpen || !existedPrediction || !match) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="blue-sky-card rounded-t-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 w-full sm:max-w-md sm:mx-4 shadow-2xl animate-scale-in max-h-[85vh] sm:max-h-[80vh] overflow-y-auto sm:mt-16 lg:mt-20">
        {/* Mobile drag handle */}
        <div className="sm:hidden w-12 h-1 bg-white/30 rounded-full mx-auto mb-4" />
        
        {/* Header - match number (e.g. Day 11) or fallback, then date, then Already Played */}
        <div className="text-center mb-4 sm:mb-5">
          <h2 className="text-lg sm:text-xl font-bold text-white mb-1">{match?.number ?? t('predictions.roomPredictions')}</h2>
          <p className="text-white/60 text-xs sm:text-sm mb-2">
            {formatDate(getMatchDate(match, true), true)}
          </p>
          <p className="text-white/80 font-semibold text-sm sm:text-base">{t('predictions.alreadyPlayed')}</p>
        </div>

        {/* Match Card - teams with icons, score in small blue box */}
        <div className="bg-white/10 rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-4 sm:mb-5">
          <div className="flex items-center justify-between gap-2 sm:gap-3">
            <div className="flex-1 text-center min-w-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-1.5 sm:mb-2">
                <span className="text-lg sm:text-xl">🏠</span>
              </div>
              <p className="text-white font-medium text-xs sm:text-sm px-1">
                <TeamNameWithFlag
                  teamName={match.teamA}
                  league={match.league}
                  side="home"
                  nameClassName="text-white font-medium text-xs sm:text-sm"
                  flagClassName="text-base leading-none flex-shrink-0"
                />
              </p>
            </div>
            <div className="flex-shrink-0 px-2 sm:px-3">
              <div className="bg-primary rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-2.5">
                <span className="text-primary-foreground font-bold text-lg sm:text-xl">
                  {match.score ?? existedPrediction.predictedScore ?? '-'}
                </span>
              </div>
            </div>
            <div className="flex-1 text-center min-w-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-1.5 sm:mb-2">
                <span className="text-lg sm:text-xl">✈️</span>
              </div>
              <p className="text-white font-medium text-xs sm:text-sm px-1">
                <TeamNameWithFlag
                  teamName={match.teamB}
                  league={match.league}
                  side="away"
                  nameClassName="text-white font-medium text-xs sm:text-sm"
                  flagClassName="text-base leading-none flex-shrink-0"
                />
              </p>
            </div>
          </div>
        </div>

        {existedPrediction.winner && (
          <p className="text-center text-white/70 text-xs sm:text-sm mb-4">
            {t('predictions.winner')} <span className="text-white font-medium">{existedPrediction.winner}</span>
          </p>
        )}

        {/* Action Buttons - Keep / Change */}
        <div className="flex gap-2 sm:gap-3">
          <button
            onClick={() => {
              onKeepClick()
              setIsOpen(false)
            }}
            className="flex-1 px-4 py-2.5 sm:px-6 sm:py-3 bg-primary hover:opacity-90 active:scale-95 text-primary-foreground rounded-xl transition-all font-semibold text-sm sm:text-base"
          >
            {t('predictions.keep')}
          </button>
          <button
            onClick={() => {
              onChangeClick()
              setIsOpen(false)
            }}
            className="flex-1 px-4 py-2.5 sm:px-6 sm:py-3 bg-primary hover:opacity-90 active:scale-95 text-primary-foreground rounded-xl transition-all font-semibold text-sm sm:text-base"
          >
            {t('predictions.change')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ExistedPredictionModal
